'use client';

import { useCartState } from '@/core/application/providers/StateMachineProvider';
import { useOrderState } from '@/core/application/providers/StateMachineProvider';
import { formatPrice } from '@/utils/formatters';
import { NotepadText } from 'lucide-react';

export const CartSummary = () => {
  const { items, isEmpty, isLoading, total } = useCartState();
  const { context: orderContext } = useOrderState();

  // FIXME: orderContext has no data. Delivery and dietary data won't show up.

  const deliveryData = orderContext.orderData?.deliveryData;
  const dietaryData = orderContext.orderData?.dietaryData;

  if (isLoading) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Summary</h2>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-gray-600">Loading your summary...</p>
        </div>
      </div>
    );
  }

  if (isEmpty || items.length === 0) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Summary</h2>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-gray-600">You have not added anything to your cart yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Summary</h2>
      <p className="text-gray-600 mb-6">Review your order below before proceeding to checkout.</p>

      <div className="space-y-6">
        {/* Order Items */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Items</h3>
          <ul className="divide-y divide-gray-100">
            {items.map((item) => (
              <li key={item.variant_id} className="py-4 first:pt-0 last:pb-0">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {item.product_name} × {item.quantity}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">SKU: {item.sku}</p>
                    {item.in_cart.selected_attributes?.length > 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        {item.in_cart.selected_attributes.map((attr) => `${attr.key}: ${attr.value}`).join(', ')}
                      </p>
                    )}
                    {item.note && (
                      <div className="flex items-start mt-2 p-3 bg-gray-50 rounded-lg">
                        <NotepadText className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-600 italic">&quot;{item.note}&quot;</span>
                      </div>
                    )}
                  </div>
                  <div className="ml-4 font-semibold text-gray-900">
                    {formatPrice(item.in_cart.price * item.quantity)}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-200">
            <span className="text-lg font-bold text-gray-900">Total</span>
            <span className="text-lg font-bold text-gray-900">{formatPrice(total)}</span>
          </div>
        </div>

        {/* Delivery Information */}
        {deliveryData && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Delivery Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Name:</span>
                <div className="text-gray-900 mt-1">{deliveryData.fullName || 'Not provided'}</div>
              </div>

              <div>
                <span className="font-medium text-gray-700">Phone:</span>
                <div className="text-gray-900 mt-1">
                  {deliveryData.phoneNumber?.replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3') || 'Not provided'}
                </div>
              </div>

              <div>
                <span className="font-medium text-gray-700">Email:</span>
                <div className="text-gray-900 mt-1">{deliveryData.email || 'Not provided'}</div>
              </div>

              <div>
                <span className="font-medium text-gray-700">Delivery Location:</span>
                <div className="text-gray-900 mt-1">
                  {deliveryData.streetAddress || 'Not provided'}, {deliveryData.city || 'TBD'},{' '}
                  {deliveryData.state || 'TBD'} {deliveryData.zipCode || ''}
                </div>
              </div>

              <div>
                <span className="font-medium text-gray-700">Delivery Date:</span>
                <div className="text-gray-900 mt-1">
                  {deliveryData.deliveryDate
                    ? new Date(deliveryData.deliveryDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'To be determined'}
                </div>
              </div>

              <div>
                <span className="font-medium text-gray-700">Delivery Time:</span>
                <div className="text-gray-900 mt-1">
                  {deliveryData.deliveryTime
                    ? new Date(`1970-01-01T${deliveryData.deliveryTime}`).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      })
                    : 'To be determined'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dietary Restrictions */}
        {dietaryData && Object.values(dietaryData).some((value) => value) && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Dietary Restrictions</h3>
            <div className="space-y-3">
              {Object.entries(dietaryData).map(([key, value]) => {
                if (key === 'additionalComments') {
                  return value ? (
                    <div key={key} className="text-sm">
                      <span className="font-medium text-gray-700">Comments:</span>
                      <div className="text-gray-900 mt-1">{value}</div>
                    </div>
                  ) : null;
                }
                return value ? (
                  <div key={key} className="flex items-center text-sm">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-3 flex-shrink-0"></span>
                    <span className="text-gray-900">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                    </span>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
